<template>
  <div class="d-flex flex-column row-gap-4">
    <div class="d-flex flex-column row-gap-2 ">
      <h1>{{ $t('project.signed-documents.SignedDocumentsHeader.signedDocuments') }}</h1>
      <p class="text--small">
        {{ $t('project.signed-documents.SignedDocumentsHeader.findSignedDocuments') }}
      </p>
    </div>
    <div class="d-flex column-gap-4">
      <SearchTextField 
        :disabled="noData"
        v-model="computedSearchQuery"
        :tooltipText="$t('project.signed-documents.SignedDocumentsHeader.searchPlaceholder')"
      />
      <app-button 
        data-locator="filters-button"
        :disabled="noData"
        type="outlined"
        variant="neutral"
        @click="isFiltersPaneOpen = true"
      >
        <app-icon-ds 
          icon="sliders"
          size="xs"
          weight="far"
          class="mr-2"
        />
        <span>{{ $t('project.signed-documents.SignedDocumentsHeader.filtersButton') }}</span>
      </app-button>
      <template v-if="display === DISPLAY.grid && filteredSignedDocuments.length > 0">
        <v-checkbox :model-value="areAllSelectedInCurrentPage"
                    :label="$t('project.signed-documents.SignedDocumentsHeader.selectAll')"
                    :indeterminate="areSomeSelectedInCurrentPage"
                    color="primary"
                    hide-details
                    data-locator="select-all-checkbox"
                    @update:model-value="$emit('toggle-all-selection')"
        />
      </template>
      <template v-if="!noResults">
        <div class="d-flex justify-end ml-auto">
          <AppTooltip :disabled="disabled">
            <template v-slot:activator="{ props }">
              <app-button 
                data-locator="grid-display-button"
                :disabled="disabled"
                type="icon"
                v-bind="props" 
                @click="$emit('update:display', DISPLAY.grid)"
              >
                <app-icon-ds 
                  :class="{'text-primary': display === DISPLAY.grid && !disabled}"
                  icon="fa-border-all"
                  weight="far"
                />
              </app-button>
            </template>
            <span>{{$t('project.signed-documents.SignedDocumentsHeader.gridDisplay')}}</span>
          </AppTooltip>
          <AppTooltip :disabled="noData">
            <template v-slot:activator="{ props }">
              <app-button 
                data-locator="table-display-button"
                :disabled="disabled"
                type="icon"
                v-bind="props"
                @click="$emit('update:display', DISPLAY.table)"
              >
                <app-icon-ds
                  :class="{'text-primary': display === DISPLAY.table && !disabled}"
                  icon="fa-list"
                  weight="far"
                />
              </app-button>
            </template>
            <span>{{$t('project.signed-documents.SignedDocumentsHeader.tableDisplay')}}</span>
          </AppTooltip>
        </div>
      </template>
    </div>
    <template v-if="selectedSignedDocuments.length > 0">
      <div class="align-center mt-2 mb-1">
        <SignedDocumentsActionBar
          :selection="selectedSignedDocuments"
          @deselect-all="unselectAllSignedDocuments"
          @selectedSignature="selectedSignatures = $event"
        />
      </div>
    </template>
    <template v-if="hasFilters">
      <SignedDocumentsHeaderFilters />
    </template>
    <SignedDocumentsFiltersPane :is-open="isFiltersPaneOpen" @close="isFiltersPaneOpen = false" />
  </div>
</template>

<script>
import { debounce } from 'lodash-es'
import { mapState, mapActions, mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import { DISPLAY } from '@/projects/display'

import SignedDocumentsFiltersPane from './pane/SignedDocumentsFiltersPane.vue'
import SignedDocumentsActionBar from './SignedDocumentsActionBar.vue'
import SignedDocumentsHeaderFilters from './SignedDocumentsHeaderFilters.vue'

export default {
  name: 'SignedDocumentsHeader',
  inheritAttrs: false,
  components: {
    AppTooltip,
    SearchTextField,
    SignedDocumentsFiltersPane,
    SignedDocumentsHeaderFilters,
    SignedDocumentsActionBar,
  },
  inject: [
    'selectedSignedDocuments',
    'unselectAllSignedDocuments',
  ],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    display: {
      type: String,
      required: true,
    },
    noData: {
      type: Boolean,
      required: true,
    },
    noResults: {
      type: Boolean,
      required: true,
    },
    areAllSelectedInCurrentPage: {
      type: Boolean,
      required: true,
    },
    areSomeSelectedInCurrentPage: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      DISPLAY,
      selectAll: false,
      isFiltersPaneOpen: false,
    }
  },
  computed: {
    ...mapState('signedDocuments', ['signedDocuments', 'searchQuery', 'filters']),
    ...mapGetters('signedDocuments', ['filteredSignedDocuments']),
    selectedSignatures: {
      get () {
        return this.selectedSignedDocuments
      },
      set (val) {
        // TODO : utilisez inject
        this.$emit('selectedSignature', val)
      },
    },
    computedSearchQuery: {
      get () { return this.searchQuery },
      set: debounce(function (val) {
        this.setSearchQuery(val)
      }, 1000),
    },
    hasFilters () {
      return this.filters.startDate || this.filters.endDate || this.filters.signatories.length > 0
    },
  },
  methods: {
    ...mapActions('signedDocuments', ['setSearchQuery']),
  },
}
</script>
